import { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Button,
  Divider,
  Group,
  Modal,
  NumberInput,
  Select,
  SimpleGrid,
  Stack,
  Text,
  Textarea,
} from "@mantine/core";
import { modals } from "@mantine/modals";
import { IconAlertTriangle, IconCheck, IconRefresh } from "@tabler/icons-react";

import {
  useApplyCommissionReconciliation,
  useCommissionReconciliationPartners,
  usePreviewCommissionReconciliation,
} from "@hooks/mutations/earnings/useCommissionReconciliation";


interface Props {
  opened: boolean;
  onClose: () => void;
  staffId: string | null;
  staffName: string | null;
}

const currency = new Intl.NumberFormat("fr-CI", {
  style: "currency",
  currency: "XOF",
  maximumFractionDigits: 0,
});

const getPreviousMonth = () => {
  const value = new Date();
  value.setUTCDate(1);
  value.setUTCMonth(value.getUTCMonth() - 1);
  return { year: value.getUTCFullYear(), month: value.getUTCMonth() + 1 };
};

export const CommissionReconciliationModal = ({
  opened,
  onClose,
  staffId,
  staffName,
}: Props) => {
  const previousMonth = useMemo(getPreviousMonth, []);
  const [year, setYear] = useState<number>(previousMonth.year);
  const [month, setMonth] = useState<string>(String(previousMonth.month));
  const [partnerId, setPartnerId] = useState<string | null>(null);
  const [reason, setReason] = useState("");
  const partners = useCommissionReconciliationPartners(staffId, opened);
  const preview = usePreviewCommissionReconciliation();
  const apply = useApplyCommissionReconciliation();

  useEffect(() => {
    if (!opened) {
      preview.reset();
      apply.reset();
      setReason("");
    }
  }, [opened]);

  useEffect(() => {
    preview.reset();
    setReason("");
    setPartnerId(null);
  }, [staffId]);

  useEffect(() => {
    if (partners.data?.length === 1) setPartnerId(partners.data[0].id);
  }, [partners.data]);

  const close = () => {
    if (!apply.isLoading) onClose();
  };

  const runPreview = () => {
    if (!staffId) return;
    setReason("");
    preview.reset();
    preview.mutate({
      staff_id: staffId,
      year,
      month: Number(month),
      partner_id: partnerId || undefined,
    });
  };

  const confirmApply = () => {
    if (!preview.data || reason.trim().length < 5) return;
    modals.openConfirmModal({
      title: "Apply commission reconciliation",
      children: (
        <Text size="sm">
          Reconcile {preview.data.payments_requiring_changes} payments for{" "}
          {preview.data.partner_name}, {monthOptions[preview.data.month - 1].label}{" "}
          {preview.data.year}?
          This changes stored financial records and cannot be undone from this screen.
        </Text>
      ),
      labels: { confirm: "Apply reconciliation", cancel: "Cancel" },
      confirmProps: { color: "red" },
      onConfirm: () =>
        apply.mutate(
          {
            reconciliation_id: preview.data!.reconciliation_id,
            reason: reason.trim(),
          },
          { onSuccess: close }
        ),
    });
  };

  const monthOptions = [
    "January", "February", "March", "April", "May", "June",
    "July", "August", "September", "October", "November", "December",
  ].map((label, index) => ({ label, value: String(index + 1) }));

  const current = new Date();
  const selectedIsPast =
    (Number.isInteger(year) &&
      year >= 2000 &&
      year < current.getUTCFullYear()) ||
    (Number.isInteger(year) &&
      year >= 2000 &&
      year === current.getUTCFullYear() &&
      Number(month) < current.getUTCMonth() + 1);

  return (
    <Modal opened={opened} onClose={close} title="Reconcile commissions" size="lg" closeOnClickOutside={!apply.isLoading}>
      <Stack>
        <Alert color="blue" icon={<IconRefresh size={18} />}>
          Reconciliation always covers the selected partner&apos;s entire calendar month. Table search,
          pagination and status filters do not limit it.
        </Alert>
        <Text size="sm"><strong>Selected doctor:</strong> {staffName || "—"}</Text>
        <Select
          label="Partner"
          placeholder={partners.isLoading ? "Loading partners…" : "Select partner"}
          data={(partners.data || []).map(partner => ({ label: partner.name, value: partner.id }))}
          value={partnerId}
          onChange={value => {
            setPartnerId(value);
            preview.reset();
            setReason("");
          }}
          disabled={partners.isLoading || (partners.data?.length || 0) <= 1}
          error={partners.isError ? "Unable to load this doctor's partners" : undefined}
        />
        <Group grow align="end">
          <Select
            label="Calendar month"
            data={monthOptions}
            value={month}
            onChange={value => {
              if (!value) return;
              setMonth(value);
              preview.reset();
              setReason("");
            }}
          />
          <NumberInput
            label="Year"
            min={2000}
            max={previousMonth.year}
            value={year}
            onChange={value => {
              setYear(Number(value));
              preview.reset();
              setReason("");
            }}
          />
          <Button onClick={runPreview} loading={preview.isLoading} disabled={!staffId || !partnerId || !selectedIsPast}>
            Preview
          </Button>
        </Group>
        {!selectedIsPast && (
          <Alert color="red" icon={<IconAlertTriangle size={18} />}>
            Current and future months cannot be reconciled.
          </Alert>
        )}

        {preview.data && (
          <>
            <Divider />
            <Text fw={600}>{preview.data.partner_name}</Text>
            <SimpleGrid cols={{ base: 2, sm: 3 }}>
              <Stack gap={0}><Text size="xs" c="dimmed">Payments analyzed</Text><Text fw={600}>{preview.data.payments_analyzed}</Text></Stack>
              <Stack gap={0}><Text size="xs" c="dimmed">Corrections required</Text><Text fw={600}>{preview.data.payments_requiring_changes}</Text></Stack>
              <Stack gap={0}><Text size="xs" c="dimmed">Payout-linked blockers</Text><Text fw={600} c={preview.data.blocked_payments ? "red" : undefined}>{preview.data.blocked_payments}</Text></Stack>
              <Stack gap={0}><Text size="xs" c="dimmed">Affected commissions before</Text><Text fw={600}>{currency.format(preview.data.old_provider_total)}</Text></Stack>
              <Stack gap={0}><Text size="xs" c="dimmed">Affected commissions after</Text><Text fw={600}>{currency.format(preview.data.new_provider_total)}</Text></Stack>
              <Stack gap={0}><Text size="xs" c="dimmed">Provider difference</Text><Text fw={600}>{currency.format(preview.data.provider_difference)}</Text></Stack>
            </SimpleGrid>

            {!preview.data.can_apply && (
              <Alert color="red" icon={<IconAlertTriangle size={18} />} title="Reconciliation blocked">
                {preview.data.blocked_payments > 0
                  ? "At least one required correction is linked to a payout. No payments will be changed."
                  : "One or more payments have no applicable provider contract. No payments will be changed."}
              </Alert>
            )}
            {preview.data.can_apply && preview.data.payments_requiring_changes === 0 && (
              <Alert color="green" icon={<IconCheck size={18} />}>No commission discrepancies were found.</Alert>
            )}
            {preview.data.can_apply && preview.data.payments_requiring_changes > 0 && (
              <>
                <Textarea
                  label="Reason for reconciliation"
                  description="This reason will be stored in the financial audit record."
                  required
                  minRows={3}
                  maxLength={500}
                  value={reason}
                  onChange={event => setReason(event.currentTarget.value)}
                />
                <Group justify="flex-end">
                  <Button variant="default" onClick={close}>Cancel</Button>
                  <Button color="red" onClick={confirmApply} loading={apply.isLoading} disabled={reason.trim().length < 5}>
                    Apply reconciliation
                  </Button>
                </Group>
              </>
            )}
          </>
        )}
      </Stack>
    </Modal>
  );
};
